import React from 'react';
import Icon from 'components/AppIcon';

const NextStepsSlide = () => {
  const launchPhases = [
    {
      phase: 'Phase 1: Beta Launch',
      duration: 'Q1 2024 (2 months)',
      status: 'ready',
      objectives: [
        'Launch beta version with 50 selected law firms',
        'Gather user feedback and optimize platform',
        'Refine onboarding and user experience',
        'Validate pricing and business model'
      ],
      deliverables: [
        'Beta platform deployment',
        'User feedback collection system',
        'Performance monitoring dashboard',
        'Initial customer testimonials'
      ]
    },
    {
      phase: 'Phase 2: Public Launch',
      duration: 'Q2 2024 (3 months)',
      status: 'planned',
      objectives: [
        'Full public platform launch',
        'Comprehensive marketing campaign',
        'Customer acquisition acceleration',
        'Partnership development'
      ],
      deliverables: [
        'Production platform deployment',
        'Marketing campaign execution',
        'Customer onboarding automation',
        'Partner integration agreements'
      ]
    },
    {
      phase: 'Phase 3: Scale & Expand',
      duration: 'Q3-Q4 2024 (6 months)',
      status: 'planned',
      objectives: [
        'Scale customer acquisition',
        'Launch mobile applications',
        'Expand feature set',
        'Regional market expansion'
      ],
      deliverables: [
        'iOS and Android apps',
        'Advanced analytics features',
        'Multi-region deployment',
        'Enterprise customer acquisition'
      ]
    }
  ];

  const investmentAsk = {
    amount: 'KES 100M',
    useOfFunds: [
      { category: 'Technology Development', percentage: 40, amount: 40000000 },
      { category: 'Marketing & Sales', percentage: 30, amount: 30000000 },
      { category: 'Team Expansion', percentage: 20, amount: 20000000 },
      { category: 'Working Capital', percentage: 10, amount: 10000000 }
    ],
    milestones: [
      'Break-even by month 18',
      '5,000 lawyers by Year 3',
      'KES 500M annual revenue by Year 3',
      'Market leadership in Kenya'
    ]
  };

  const partnerships = [
    {
      type: 'Strategic Partners',
      organizations: [
        'Law Society of Kenya',
        'Kenya Bankers Association',
        'Major Law Firms',
        'Courts & Judiciary'
      ],
      icon: 'Handshake'
    },
    {
      type: 'Technology Partners',
      organizations: [
        'Cloud Infrastructure Providers',
        'Payment Gateway Partners',
        'Security Compliance Partners',
        'Integration Partners'
      ],
      icon: 'Cpu'
    },
    {
      type: 'Channel Partners',
      organizations: [
        'Legal Training Institutes',
        'Professional Associations',
        'Business Consultancies',
        'Technology Resellers'
      ],
      icon: 'Users'
    }
  ];

  const contactInfo = {
    company: 'LawDesk KE',
    email: 'support@lawdesk.co.ke',
    phone: '0727265443',
    website: 'lawdesk.co.ke',
    address: 'Westlands, Nairobi, Kenya'
  };

  const formatCurrency = (amount) => {
    return `KES ${(amount / 1000000)?.toFixed(0)}M`;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'ready': 'text-success-600 bg-success-50',
      'planned': 'text-primary bg-primary-50',
      'in-progress': 'text-warning-600 bg-warning-50'
    };
    return colorMap?.[status] || colorMap?.['planned'];
  };

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-heading font-bold text-text-primary mb-4">
            Next Steps & Call to Action
          </h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Ready to transform Kenya's legal services industry together
          </p>
        </div>

        {/* Launch Roadmap */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Launch Roadmap
          </h2>
          <div className="space-y-6">
            {launchPhases?.map((phase, index) => (
              <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border">
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      phase?.status === 'ready' ? 'bg-success-500' : 
                      phase?.status === 'in-progress' ? 'bg-warning-500' : 'bg-primary'
                    }`}>
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-heading font-semibold text-text-primary">
                          {phase?.phase}
                        </h3>
                        <p className="text-text-secondary">{phase?.duration}</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(phase?.status)}`}>
                        {phase?.status === 'ready' ? 'Ready to Execute' : 
                         phase?.status === 'in-progress' ? 'In Progress' : 'Planned'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-heading font-semibold text-text-primary mb-3">Objectives</h4>
                        <ul className="space-y-2">
                          {phase?.objectives?.map((objective, objIndex) => (
                            <li key={objIndex} className="flex items-start space-x-2 text-sm text-text-secondary">
                              <Icon name="Target" size={14} className="text-primary mt-1 flex-shrink-0" />
                              <span>{objective}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-heading font-semibold text-text-primary mb-3">Key Deliverables</h4>
                        <ul className="space-y-2">
                          {phase?.deliverables?.map((deliverable, delIndex) => (
                            <li key={delIndex} className="flex items-start space-x-2 text-sm text-text-secondary">
                              <Icon name="CheckCircle" size={14} className="text-success-600 mt-1 flex-shrink-0" />
                              <span>{deliverable}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Investment Ask */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Investment Opportunity
          </h2>
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-8">
            <div className="text-center mb-8">
              <div className="text-5xl font-heading font-bold text-primary mb-4">
                {investmentAsk?.amount}
              </div>
              <p className="text-xl text-text-secondary">Seeking investment to accelerate growth</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-4">Use of Funds</h3>
                <div className="space-y-3">
                  {investmentAsk?.useOfFunds?.map((fund, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/70 rounded-lg">
                      <span className="text-text-primary">{fund?.category}</span>
                      <div className="text-right">
                        <div className="font-bold text-primary">{fund?.percentage}%</div>
                        <div className="text-sm text-text-secondary">{formatCurrency(fund?.amount)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-4">Key Milestones</h3>
                <div className="space-y-3">
                  {investmentAsk?.milestones?.map((milestone, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-white/70 rounded-lg">
                      <Icon name="Award" size={20} className="text-primary flex-shrink-0" />
                      <span className="text-text-primary">{milestone}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Strategic Partnerships */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Strategic Partnership Opportunities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {partnerships?.map((partnership, index) => (
              <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border">
                <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={partnership?.icon} size={24} className="text-secondary-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-text-primary mb-4">
                  {partnership?.type}
                </h3>
                <ul className="space-y-2">
                  {partnership?.organizations?.map((org, orgIndex) => (
                    <li key={orgIndex} className="flex items-center space-x-2 text-sm text-text-secondary">
                      <Icon name="Dot" size={12} className="text-primary" />
                      <span>{org}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-primary rounded-lg p-8 text-white text-center">
          <h2 className="text-3xl font-heading font-bold mb-4">
            Ready to Transform Legal Services in Kenya?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
            Join us in revolutionizing how legal services are delivered across Kenya. 
            Let's build the future of legal practice management together.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <div className="bg-white/10 rounded-lg p-4">
              <Icon name="Mail" size={24} className="mx-auto mb-2" />
              <p className="text-sm">{contactInfo?.email}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <Icon name="Phone" size={24} className="mx-auto mb-2" />
              <p className="text-sm">{contactInfo?.phone}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <Icon name="Globe" size={24} className="mx-auto mb-2" />
              <p className="text-sm">{contactInfo?.website}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <Icon name="MapPin" size={24} className="mx-auto mb-2" />
              <p className="text-sm">{contactInfo?.address}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <Icon name="Building" size={24} className="mx-auto mb-2" />
              <p className="text-sm">{contactInfo?.company}</p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-primary px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-smooth">
              Schedule a Demo
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-white/10 transition-smooth">
              Request Investment Deck
            </button>
          </div>
        </div>

        {/* Thank You */}
        <div className="mt-12 text-center">
          <h2 className="text-3xl font-heading font-bold text-text-primary mb-4">
            Thank You
          </h2>
          <p className="text-xl text-text-secondary">
            Questions & Discussion
          </p>
        </div>
      </div>
    </div>
  );
};

export default NextStepsSlide;